/**
 * @fileoverview Scrollable feed of today's orders grouped by orderId.
 * @module features/daily-summary/components/OrderFeed
 */

import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Package } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { useReportingCurrency } from '@/features/settings/context/OrgSettingsProvider'
import { formatMoney } from '@/shared/utils/money'
import { useTodaysOrders } from '@/features/daily-summary/hooks/useTodaysOrders'
import { OrderGroupCard } from './OrderGroup'

export function OrderFeed(): ReactElement {
  const { t } = useTranslation('features-home')
  const { orders, totalOrders, totalRevenue, isLoading } = useTodaysOrders()
  const { currency } = useReportingCurrency()
  const cur = currency ?? 'PLN'

  const avg = totalOrders > 0 ? totalRevenue / totalOrders : 0

  if (isLoading) {
    return (
      <div className="bg-card border border-border/50 rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-border/50 flex justify-between items-center">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="p-5 space-y-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-card border border-border/50 rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-5 border-b border-border/50">
        <div className="flex items-center gap-2 text-[0.95rem] font-semibold text-fg-bright">
          <span className="w-2 h-2 rounded-full bg-positive animate-pulse" />
          {t('orderFeed.title')}
        </div>
        <span className="font-mono text-[0.8rem] font-semibold text-fg-muted bg-foreground/5 px-3 py-1 rounded-full">
          {t('orderFeed.ordersCount', { count: totalOrders })} &middot; {t('orderFeed.avgOrder', { value: formatMoney(avg, cur, undefined, 0, 0) })}
        </span>
      </div>

      {/* Orders list */}
      {orders.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-12 text-muted-foreground/50">
          <Package className="w-8 h-8" />
          <span className="text-sm">{t('orderFeed.empty')}</span>
        </div>
      ) : (
        <div className="max-h-[380px] overflow-y-auto overscroll-contain pr-1 scrollbar-subtle">
          {orders.map((order) => (
            <OrderGroupCard key={order.orderId} order={order} />
          ))}
        </div>
      )}
    </div>
  )
}
